import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { DatePicker } from './ui/DatePicker';
import { Toggle } from './ui/Toggle';
import { ArrowLeft, Save, Plus } from 'lucide-react';
import { FeeSlabRow, FeeSlab } from './FeeSlabRow';
interface FeeProfileFormProps {
  onBack: () => void;
  onSave: () => void;
}
export function FeeProfileForm({
  onBack,
  onSave
}: FeeProfileFormProps) {
  const [profileId] = useState(`F${Math.random().toString(36).substr(2, 3).toUpperCase()}`);
  // Fee Class State
  const [feeClass, setFeeClass] = useState<'Transaction Fees' | 'Event Fees'>('Transaction Fees');
  // Header State for Transaction Fees
  const [transactionFeeData, setTransactionFeeData] = useState({
    code: '',
    name: '',
    description: '',
    fee: '',
    category: 'Charges',
    status: 'active',
    effectiveDate: ''
  });
  // Header State for Event Fees
  const [eventFeeData, setEventFeeData] = useState({
    code: '',
    name: '',
    fee: '',
    feeEvent: '',
    status: 'active',
    effectiveDate: '',
    description: '',
    feeType: 'Percentage',
    amount: '',
    glAccount: '',
    recurring: false,
    recurringFrequency: ''
  });
  // Slabs State (for Transaction Fees only)
  const [slabs, setSlabs] = useState<FeeSlab[]>([{
    id: Math.random().toString(36).substr(2, 9),
    feeType: 'Percentage',
    feeValue: '',
    slabLow: '0',
    slabHigh: '',
    minCap: '',
    maxCap: '',
    glAccount: '',
    taxProfiles: []
  }]);
  const handleTransactionFeeChange = (field: string, value: string) => {
    setTransactionFeeData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleEventFeeChange = (field: string, value: string | boolean) => {
    setEventFeeData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleAddSlab = () => {
    const lastSlab = slabs[slabs.length - 1];
    const newLow = lastSlab && lastSlab.slabHigh ? (parseInt(lastSlab.slabHigh) + 1).toString() : '0';
    setSlabs([...slabs, {
      id: Math.random().toString(36).substr(2, 9),
      feeType: 'Percentage',
      feeValue: '',
      slabLow: newLow,
      slabHigh: '',
      minCap: '',
      maxCap: '',
      glAccount: '',
      taxProfiles: []
    }]);
  };
  const handleRemoveSlab = (id: string) => {
    if (slabs.length > 1) {
      setSlabs(slabs.filter(s => s.id !== id));
    }
  };
  const handleSlabChange = (id: string, updates: Partial<FeeSlab>) => {
    setSlabs(slabs.map(s => s.id === id ? {
      ...s,
      ...updates
    } : s));
  };
  return <div className="flex flex-col h-full bg-gray-50" data-id="element-178">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10" data-id="element-179">
        <div className="flex items-center" data-id="element-180">
          <Button variant="ghost" onClick={onBack} className="mr-4" data-id="element-181">
            <ArrowLeft className="h-5 w-5 mr-2" data-id="element-182" />
            Back to List
          </Button>
          <div data-id="element-183">
            <h1 className="text-xl font-bold text-gray-900" data-id="element-184">New Fee Profile</h1>
            <p className="text-sm text-gray-500" data-id="element-185">
              Configure fee structure and definitions
            </p>
          </div>
        </div>
        <div className="flex space-x-3" data-id="element-186">
          <Button variant="secondary" onClick={onBack} data-id="element-187">
            Cancel
          </Button>
          <Button onClick={onSave} data-id="element-188">
            <Save className="h-4 w-4 mr-2" data-id="element-189" />
            Save Profile
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 max-w-5xl mx-auto w-full space-y-8" data-id="element-190">
        {/* Fee Class Selection */}
        <section className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-191">
          <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-192">
            Fee Class
          </h2>
          <div className="flex space-x-4" data-id="element-193">
            <button onClick={() => setFeeClass('Transaction Fees')} className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${feeClass === 'Transaction Fees' ? 'border-blue-600 bg-blue-50 text-blue-900' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-194">
              <div className="font-medium" data-id="element-195">Transaction Fees</div>
              <div className="text-xs text-gray-500 mt-1" data-id="element-196">
                Slab-based fee structure
              </div>
            </button>
            <button onClick={() => setFeeClass('Event Fees')} className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${feeClass === 'Event Fees' ? 'border-blue-600 bg-blue-50 text-blue-900' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-197">
              <div className="font-medium" data-id="element-198">Event Fees</div>
              <div className="text-xs text-gray-500 mt-1" data-id="element-199">
                Event-based fee structure
              </div>
            </button>
          </div>
        </section>

        {/* Transaction Fees Form */}
        {feeClass === 'Transaction Fees' && <>
            <section className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-100" data-id="element-201">
                Fee Profile Header
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-202">
                <div className="space-y-1.5" data-id="element-203">
                  <label className="text-sm font-medium text-gray-700" data-id="element-204">
                    Fee ID
                  </label>
                  <div className="flex h-10 w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500" data-id="element-205">
                    {profileId}
                  </div>
                </div>

                <Input label="Fee Code" value={transactionFeeData.code} onChange={e => handleTransactionFeeChange('code', e.target.value)} placeholder="e.g., FEE-PROC-001" required data-id="element-206" />

                <Input label="Fee Name" value={transactionFeeData.name} onChange={e => handleTransactionFeeChange('name', e.target.value)} placeholder="e.g., Processing Fee" required data-id="element-207" />

                <Select label="Fee" value={transactionFeeData.fee} onChange={e => handleTransactionFeeChange('fee', e.target.value)} options={[{
              value: 'IBFT Fees',
              label: 'IBFT Fees'
            }, {
              value: 'International Fees',
              label: 'International Fees'
            }]} required data-id="element-208" />

                <Select label="Fee Category" value={transactionFeeData.category} onChange={e => handleTransactionFeeChange('category', e.target.value)} options={[{
              value: 'Charges',
              label: 'Charges'
            }]} required data-id="element-209" />

                <Select label="Fee Status" value={transactionFeeData.status} onChange={e => handleTransactionFeeChange('status', e.target.value)} options={[{
              value: 'active',
              label: 'Active'
            }, {
              value: 'inactive',
              label: 'Inactive'
            }]} required data-id="element-210" />

                <DatePicker label="Effective Date" value={transactionFeeData.effectiveDate} onChange={e => handleTransactionFeeChange('effectiveDate', e.target.value)} required data-id="element-211" />

                <div className="md:col-span-2" data-id="element-212">
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block" data-id="element-213">
                    Fee Description
                  </label>
                  <textarea className="flex min-h-[80px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Describe the purpose and policy..." value={transactionFeeData.description} onChange={e => handleTransactionFeeChange('description', e.target.value)} data-id="element-214" />
                </div>
              </div>
            </section>

            <section className="space-y-4 animate-in fade-in slide-in-from-bottom-6 duration-500 delay-100" data-id="element-215">
              <div className="flex items-center justify-between" data-id="element-216">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-217">
                  Fee Slab Configuration
                </h2>
                <Button onClick={handleAddSlab} size="sm" variant="secondary" data-id="element-218">
                  <Plus className="h-4 w-4 mr-2" data-id="element-219" />
                  Add Slab
                </Button>
              </div>

              <div className="space-y-4" data-id="element-220">
                {slabs.map((slab, index) => <FeeSlabRow key={slab.id} slab={slab} index={index} onChange={handleSlabChange} onRemove={handleRemoveSlab} data-id="element-221" />)}
              </div>
            </section>
          </>}

        {/* Event Fees Form */}
        {feeClass === 'Event Fees' && <section className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-222">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-100" data-id="element-223">
              Event Fee Configuration
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-224">
              <div className="space-y-1.5" data-id="element-225">
                <label className="text-sm font-medium text-gray-700" data-id="element-226">
                  Fee ID
                </label>
                <div className="flex h-10 w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500" data-id="element-227">
                  {profileId}
                </div>
              </div>

              <Input label="Fee Code" value={eventFeeData.code} onChange={e => handleEventFeeChange('code', e.target.value)} placeholder="e.g., FEE-SMS-001" required data-id="element-228" />

              <Input label="Fee Name" value={eventFeeData.name} onChange={e => handleEventFeeChange('name', e.target.value)} placeholder="e.g., SMS Notification Fee" required data-id="element-229" />

              <Select label="Fee" value={eventFeeData.fee} onChange={e => handleEventFeeChange('fee', e.target.value)} options={[{
            value: 'Standard SMS Fee',
            label: 'Standard SMS Fee'
          }, {
            value: 'SMS Fee for High Profile Product',
            label: 'SMS Fee for High Profile Product'
          }, {
            value: 'Maintenance Fee',
            label: 'Maintenance Fee'
          }]} required data-id="element-230" />

              <Select label="Fee Event" value={eventFeeData.feeEvent} onChange={e => handleEventFeeChange('feeEvent', e.target.value)} options={[{
            value: 'SMS Charges',
            label: 'SMS Charges'
          }, {
            value: 'Maintenance Fees',
            label: 'Maintenance Fees'
          }, {
            value: 'First Disbursement Fees',
            label: 'First Disbursement Fees'
          }]} required data-id="element-231" />

              <Select label="Fee Status" value={eventFeeData.status} onChange={e => handleEventFeeChange('status', e.target.value)} options={[{
            value: 'active',
            label: 'Active'
          }, {
            value: 'inactive',
            label: 'Inactive'
          }]} required data-id="element-232" />

              <DatePicker label="Effective Date" value={eventFeeData.effectiveDate} onChange={e => handleEventFeeChange('effectiveDate', e.target.value)} required data-id="element-233" />

              <div className="md:col-span-2" data-id="element-234">
                <label className="text-sm font-medium text-gray-700 mb-1.5 block" data-id="element-235">
                  Fee Description
                </label>
                <textarea className="flex min-h-[80px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Describe the event fee purpose..." value={eventFeeData.description} onChange={e => handleEventFeeChange('description', e.target.value)} data-id="element-236" />
              </div>

              <Select label="Fee Type" value={eventFeeData.feeType} onChange={e => handleEventFeeChange('feeType', e.target.value)} options={[{
            value: 'Percentage',
            label: 'Percentage'
          }, {
            value: 'Fixed',
            label: 'Fixed'
          }]} required data-id="element-237" />

              <Input label="Amount" type="number" value={eventFeeData.amount} onChange={e => handleEventFeeChange('amount', e.target.value)} placeholder={eventFeeData.feeType === 'Fixed' ? '50' : '2.5'} required data-id="element-238" />

              <Select label="GL Account" value={eventFeeData.glAccount} onChange={e => handleEventFeeChange('glAccount', e.target.value)} options={[{
            value: '4100',
            label: '4100 - Fee Income'
          }, {
            value: '4200',
            label: '4200 - Event Fee Income'
          }, {
            value: '4300',
            label: '4300 - Service Charges'
          }]} required data-id="element-239" />

              <div className="md:col-span-2 space-y-4 pt-4 border-t border-gray-100" data-id="element-240">
                <Toggle label="Recurring" checked={eventFeeData.recurring} onChange={checked => handleEventFeeChange('recurring', checked)} data-id="element-241" />

                {eventFeeData.recurring && <Select label="Recurring Frequency" value={eventFeeData.recurringFrequency} onChange={e => handleEventFeeChange('recurringFrequency', e.target.value)} options={[{
              value: 'Daily',
              label: 'Daily'
            }, {
              value: 'Monthly',
              label: 'Monthly'
            }, {
              value: 'Yearly',
              label: 'Yearly'
            }]} required data-id="element-242" />}
              </div>
            </div>
          </section>}
      </div>
    </div>;
}